import { ref } from "vue";
import useCheckout from "./useCheckout";
import type {PaymentIntent, PaymentIntentResult, PaymentMethodResult, SetupIntent, SetupIntentResult, Stripe} from '@stripe/stripe-js';
import {loadStripe} from '@stripe/stripe-js/pure';
import useGlobalAlert from "./useGlobalAlert";

let stripe: Stripe|null = null;
const paymentConfirmationError = ref();
const sourceResponse = ref<PaymentMethodResult|null>();
const iframeSource = ref<HTMLIFrameElement|null>(null);
const pendingPayment = ref<(value: PaymentMethodResult|null) => void>();

export function useStripePayment(){
	const {wooCart, wooCheckout} = useCheckout();
	const {addAlert} = useGlobalAlert();

	function setSourceResponse(value: PaymentMethodResult|null){
		sourceResponse.value = value;
		if(pendingPayment.value){
			pendingPayment.value(value);
		}
	}

	loadStripe.setLoadParameters({advancedFraudSignals: false});
	function initStripe(){
		if(stripe || !wooCart.cart.value?.extensions?.itlp?.stripe_pkey) return;

		return loadStripe(wooCart.cart.value.extensions.itlp.stripe_pkey as string, {}).then(s => {
			if(!s) return;
			stripe = s;
		});
	}

	async function createSource(): Promise<PaymentMethodResult|null>{
		const billingData: any = {
			address: {
				line1: wooCart.cart.value!.billing_address.address_1,
				line2: wooCart.cart.value!.billing_address.address_2,
				city: wooCart.cart.value!.billing_address.city,
				state: wooCart.cart.value!.billing_address.state,
				postal_code: wooCart.cart.value!.billing_address.postcode,
				country: wooCart.cart.value!.billing_address.country
			}
		};
		if(wooCart.cart.value!.billing_address.email){
			billingData.email = wooCart.cart.value!.billing_address.email;
		}
		if(wooCart.cart.value!.billing_address.phone){
			billingData.phone = wooCart.cart.value!.billing_address.phone;
		}
		if(wooCart.cart.value!.billing_address.first_name || wooCart.cart.value!.billing_address.last_name){
			billingData.name = `${wooCart.cart.value!.billing_address.first_name} ${wooCart.cart.value!.billing_address.last_name}`;
		}

		return new Promise<PaymentMethodResult|null>((resolve, reject) => {
			pendingPayment.value = resolve;
			if(!iframeSource.value?.contentWindow){
				addAlert('Unexpected Error', 5000);
				reject(new Error('Stripe not initialized'));
				return;
			}
			iframeSource.value?.contentWindow?.postMessage({type: 'pay', billingData});
		});
	}

	async function getPaymentData(){
		const sourceResponse = await createSource();
		const sourceId = sourceResponse?.paymentMethod?.id ?? (sourceResponse as any)?.source?.id;
		if(!sourceId){
			throw new Error('Missing Payment Method');
		}
		const data = {
			'wc-stripe-payment-method': sourceId,
			payment_method: 'stripe',
			billing_email: wooCart.cart.value!.billing_address.email,
			billing_first_name: wooCart.cart.value!.billing_address.first_name ?? '',
			billing_last_name: wooCart.cart.value!.billing_address.last_name ?? '',
			'wc-stripe-new-payment-method': true
		};

		return Object.entries(data).map(v => ({key: v[0], value: v[1]}));
	}

	/**
	 * New version of stripe use a redirect value with all info inside
	 */
	async function newConfirmStripeIntentByRedirect(redirectValue: string, paymentMethodId = null) {
		if(!stripe){
			console.warn('cant handle stripe payment complete, stripe not initialized');
			return false;
		}
		// Formato: #wc-stripe-confirm-(pi|si):ORDER_ID:CLIENT_SECRET:NONCE
		const match = redirectValue.match(/#wc-stripe-confirm-(pi|si):(.+):(.+):(.+)$/);

		// Nessun intent da confermare (pagamento già completato lato server)
		if (!match){
			// handle redirect? where?
			return true;
		}

		const isSetup     = match[1] === 'si';
		const orderId     = match[2];
		const clientSecret = match[3];
		const nonce       = match[4];

		// Conferma l'intent con Stripe.js — senza redirect browser
		const stripeResult = isSetup
			? await stripe.confirmCardSetup(clientSecret)
			: await stripe.confirmCardPayment(clientSecret);

		if (stripeResult.error){
			paymentConfirmationError.value = stripeResult.error
			return false;
		}

		const intentId = isSetup ? (stripeResult as SetupIntentResult).setupIntent?.id : (stripeResult as PaymentIntentResult).paymentIntent?.id;

		// Notifica WooCommerce: intent confermato → aggiorna stato ordine
		const body = new URLSearchParams({
			order_id:    orderId,
			intent_id:   intentId || '',
			_ajax_nonce: nonce,
		});
		if (paymentMethodId) body.set('payment_method_id', paymentMethodId);

		// confirm the payment on woocommerce and mode the order status without waiting the webhook
		let json: any = null;
		for(let attempt = 0; attempt < 3; attempt++){
			if(attempt > 0) await new Promise(resolve => setTimeout(resolve, 1500 * attempt));
			try{
				const response = await fetch('/?wc-ajax=wc_stripe_update_order_status', {
					method:  'POST',
					headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
					body,
				});
				json = await response.json();
			}catch(e){
				json = null;
				continue;
			}
			if(json?.success) break;
		}

		if (!json?.success){
			paymentConfirmationError.value = new Error(json?.data?.error?.message ?? 'Payment failed');
			return false;
		}

		window.location.href = json.data.return_url;
		wooCart.requesting.value = 'redirecting';
		return true;
	}

	async function handleStripePaymentComplete(){
		paymentConfirmationError.value = undefined;
		if(!wooCheckout.checkout.value) return true;
		if(wooCheckout.checkout.value.payment_method != 'stripe') return true;
		if(wooCheckout.checkout.value.payment_result.payment_status != 'success') return true;
		if(!stripe){
			console.warn('cant handle stripe payment complete, stripe not initialized');
			return false;
		}
		const details = Object.values(wooCheckout.checkout.value?.payment_result.payment_details).reduce((carry, entry) => {
			carry[entry.key] = entry.value;
			return carry;
		}, {} as Record<string, string|boolean>)
		if(typeof(details.redirect) === 'string' && details.redirect.startsWith('#wc-stripe-confirm-')){
			return newConfirmStripeIntentByRedirect(details.redirect, details.payment_method);
		}
		if(!details.payment_intent_secret && !details.setup_intent_secret){
			if(details.result == 'success' && details.redirect) window.location.href = details.redirect;
			return true;
		}
		const isSetupIntent = !! details.setup_intent_secret;
		const intentSecret = isSetupIntent ? details.setup_intent_secret : details.payment_intent_secret;
		const stripeMethod = stripe[isSetupIntent ? 'confirmCardSetup' : 'confirmCardPayment'];
		
		let stripeResponse;
		try{
			stripeResponse = await stripeMethod(intentSecret);
		}catch(e: any){
			paymentConfirmationError.value = e;
			console.error(e);
			if(details.verification_endpoint) window.fetch(details.verification_endpoint + '&is_ajax');
			// what todo here? retry?
			return false;
		}

		if(stripeResponse.error){
			paymentConfirmationError.value = stripeResponse.error;
			return false;
		}
		const intent: SetupIntent|PaymentIntent = (stripeResponse as any)[isSetupIntent ? 'setupIntent' : 'paymentIntent'];
		
		if(details.verification_endpoint){
			let verificationUrl = details.verification_endpoint as string;
			if(verificationUrl.includes('redirect_to=') && !verificationUrl.match(/redirect_to=[^&]+/) && wooCheckout.checkout.value.payment_result.redirect_url){
				verificationUrl = verificationUrl.replace(/redirect_to=/, 'redirect_to=' + encodeURIComponent(wooCheckout.checkout.value.payment_result.redirect_url));
			}
			window.location.href = verificationUrl;
			wooCart.requesting.value = 'redirecting';
		}else{
			window.location.href = wooCheckout.checkout.value.payment_result.redirect_url;
			wooCart.requesting.value = 'redirecting';
		}
		return true;
	}

	return {
		initStripe,
		getStripe(){ return stripe},
		getPaymentData,
		handleStripePaymentComplete,
		setSourceResponse,
		paymentConfirmationError,
		iframeSource
	};
}